import React, { useMemo, useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Dimensions,
  FlatList,
  TouchableOpacity
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../theme";
import TabbedNavigation from "../../components/TabbedNavigation";
import LHFilter from "../../components/LHFilter";
import LHDataTable from "../../components/LHDataTable";
import FontFamily from "../../constants/fonts";
import { getResponsiveItemsPerRow } from "./utils";
import ObservationCard from "./ObservationCard";
// Client-side filtering is no longer used; API results are shown directly
import MobileFilterOverlay from "../../components/MobileFilterOverlay";

interface ObservationMetricsProps {
  tabs: any;
  activeTab: string;
  setActiveTab: (tabId: string) => void;
  filterOptions: any;
  handleFilterChange: (key: string, value: any) => void;
  isLoading: boolean;
  apiError: string | null;
  observationsData: any[];
  tableColumns: any;
  handleRowClick: (rowData: any) => void;
  applyFilters?: () => Promise<void>;
  resetFilters?: () => void;
  // Mobile filter props
  showMobileFilterButton?: boolean;
  onMobileFilterPress?: () => void;

}

const ObservationMetrics: React.FC<ObservationMetricsProps> = ({
  tabs,
  activeTab,
  setActiveTab,
  filterOptions,
  handleFilterChange,
  isLoading,
  apiError,
  observationsData,
  tableColumns,
  handleRowClick,
  applyFilters,
  resetFilters,
  showMobileFilterButton = false,
  onMobileFilterPress,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const surfaceBg = isDark ? colors.background.surface : '#FFFFFF';
  const pageBg = isDark ? colors.background.page : '#F5F9FF';
  // Initialize localFilters from filterOptions selectedOptionId to show current selections
  const [localFilters, setLocalFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    filterOptions?.forEach((option: any) => {
      if (option.selectedOptionId) {
        const selectedOption = option.options.find((opt: any) => opt.id === option.selectedOptionId);
        if (selectedOption) {
          initialFilters[option.key] = selectedOption;
        }
      }
    });
    return initialFilters;
  });
  const [isMobileFilterOverlayVisible, setMobileFilterOverlayVisible] = useState(false);

  // Update localFilters when filterOptions change to reflect Redux state changes
  useEffect(() => {
    const updatedFilters: Record<string, any> = {};
    filterOptions?.forEach((option: any) => {
      if (option.selectedOptionId) {
        const selectedOption = option.options.find((opt: any) => opt.id === option.selectedOptionId);
        if (selectedOption) {
          updatedFilters[option.key] = selectedOption;
        }
      }
    });
    setLocalFilters(updatedFilters);
  }, [filterOptions]);

  // Add effect to clear local filters when all filters are reset
  useEffect(() => {
    const hasAnySelection = filterOptions?.some((option: any) => option.selectedOptionId);
    if (!hasAnySelection) {
      setLocalFilters({});
    }
  }, [filterOptions]);

  const handleMobileFilterPress = () => {
    setMobileFilterOverlayVisible(true);
  };

  const handleMobileFilterCancel = () => {
    setMobileFilterOverlayVisible(false);
  };

  const handleMobileFilterProceed = async (nextFilters: Record<string, any>) => {
    setLocalFilters(nextFilters);
    Object.entries(nextFilters).forEach(([key, value]) => {
      handleFilterChange(key, value);
    });
    setMobileFilterOverlayVisible(false);
  };

  // Mobile pagination state for card view
  const [currentPage, setCurrentPage] = useState(1);
  const [displayStart, setDisplayStart] = useState(1);
  const rowsPerPage = 10;

  // Compute iOS mobile boolean inside the component to avoid module-level reference issues
  const windowWidth = Dimensions.get("window").width;
const isMobileIos = Platform.OS === "ios" && windowWidth < 768;
const isSmallScreen = windowWidth < 768; 
  // Unified filter change handler: update local selected values and trigger API (via parent)
  const onFilterChange = (key: string, value: any) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
    handleFilterChange(key, value);
  };

  // Use server-filtered observations data directly
  const filteredObservations = useMemo(() => {
    return observationsData;
  }, [observationsData]);

  // Ensure currentPage does not exceed total pages when data shrinks
  const totalPages = Math.max(
    1,
    Math.ceil((filteredObservations?.length || 0) / rowsPerPage)
  );

  // Reset to first page whenever the table data changes
  useEffect(() => {
    setCurrentPage(1);
    setDisplayStart(1);
  }, [filteredObservations]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedData = isMobileIos
    ? filteredObservations.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      )
    : filteredObservations;

  // Card separator component
  const CardSeparator = () => <View style={{ height: 8 }} />;

  // If on iOS mobile and mobile filter overlay is visible, render only the overlay
  // so the rest of the page is not shown underneath (modal behavior).
  if (isMobileIos && isMobileFilterOverlayVisible) {
    return (
      <View style={[styles.metricsWrapper, { backgroundColor: pageBg }]}> 
        <MobileFilterOverlay
          visible={isMobileFilterOverlayVisible}
          onDismiss={handleMobileFilterCancel}
          filters={filterOptions}
          onFilterChange={onFilterChange}
          onProceed={handleMobileFilterProceed}
          onCancel={handleMobileFilterCancel}
          currentFilters={localFilters}
        />
      </View>
    );
  }

  return (
    <View style={[styles.metricsWrapper, { backgroundColor: pageBg }]}> 
      {/* Previous Observations Section */}
      <View style={[styles.TabbedNavigationContainer, { backgroundColor: surfaceBg }, isDark && Platform.OS === 'web' && { background: surfaceBg } as any]}>
        <TabbedNavigation
          tabs={tabs}
          initialActiveTab="previous-observations"
          onTabChange={setActiveTab}
        />
      </View>

      {/* Conditionally render Filters and Data Table Sections */}
      {activeTab === "previous-observations" && (
        <>
          {/* Filters Section */}
       
          {/* Show loading message or error if applicable */}
          {isLoading && (
            <View style={[styles.messageContainer, isDark && { backgroundColor: pageBg }]}> 
              <Text style={[styles.loadingText, isDark && { color: colors.text.primary }]}>Loading observation data...</Text>
            </View>
          )}

          {apiError && (
            <View style={[styles.messageContainer, isDark && { backgroundColor: pageBg }]}> 
              <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>Error: {apiError}</Text>
            </View>
          )}

          {/* Data Table Section */}
          <View style={[
            styles.dataTableContainer,
            isMobileIos && styles.dataTableContainerMobile,
            { backgroundColor: surfaceBg },
            isDark && Platform.OS === 'web' && { background: surfaceBg } as any,
          ]}>
            <View
              style={[
                styles.headerBox,
                isMobileIos && styles.headerBoxMobileSpacing,
              ]}
            >
              <Text style={[styles.headerTitle, isDark && { color: '#FFFFFF' }]}>Observation List</Text>
              {/* Mobile filter button - only show when prop is true */}
              {showMobileFilterButton && (
                <TouchableOpacity
                  style={styles.mobileFilterButton}
                  onPress={handleMobileFilterPress}
                >
                  <MaterialIcons name="filter-list" size={20} color="#FFFFFF" />
                  <Text style={styles.mobileFilterButtonText}>Filter</Text>
                </TouchableOpacity>
              )}
            </View>
            {/* Hide regular filters when mobile filter button is shown */}
            {!showMobileFilterButton && (
              <View style={styles.filterContainer}>
                <LHFilter
                  filters={filterOptions}
                  onFilterChange={onFilterChange}
                  itemsPerRow={getResponsiveItemsPerRow(4)}
                  onClearAllFilters={resetFilters}
                />
              </View>
            )}

            {/* Mobile card view or desktop table view */}
            {isMobileIos ? (
              <>
                <FlatList
                  data={isLoading ? [] : paginatedData}
                  keyExtractor={(item, index) =>
                    item.id?.toString() || index.toString()
                  }
                  renderItem={({ item }) => (
                    <ObservationCard
                      item={item}
                      columns={tableColumns}
                      onRowClick={handleRowClick}
                    />
                  )}
                  showsVerticalScrollIndicator={false}
                  ItemSeparatorComponent={CardSeparator}
                  scrollEnabled={false}
                  removeClippedSubviews={false}
                  maxToRenderPerBatch={15}
                  initialNumToRender={15}
                  windowSize={15}
                />

                {/* Mobile pagination controls */}
                <View style={styles.mobilePaginationContainer}>
                  <View style={[styles.mobilePaginationBox, isDark && { borderColor: colors.border.default }]}> 
                    <TouchableOpacity
                      disabled={displayStart === 1 && currentPage === 1}
                      onPress={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        setDisplayStart(Math.max(1, newPage));
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        displayStart === 1 &&
                          currentPage === 1 &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-prev-page-button"
                      accessibilityLabel="Previous page"
                    >
                      <MaterialIcons
                        name="chevron-left"
                        size={20}
                        color={
                          displayStart === 1 && currentPage === 1
                            ? "#ADB5BD"
                            : isDark
                            ? "#FFFFFF"
                            : "#495057"
                        }
                      />
                    </TouchableOpacity>

                    <View style={styles.mobilePaginationCell}>
                      <Text style={[styles.mobilePaginationCellText, isDark && { color: '#D1D5DB' }]}>
                        Page {currentPage} of {totalPages}
                      </Text>
                    </View>

                    <TouchableOpacity
                      disabled={currentPage >= totalPages}
                      onPress={() => {
                        const newPage = Math.min(totalPages, currentPage + 1);
                        setCurrentPage(newPage);
                        setDisplayStart(newPage);
                      }}
                      style={[
                        styles.mobilePaginationCell,
                        styles.mobilePaginationArrowCell,
                        currentPage >= totalPages &&
                          styles.mobilePaginationCellDisabled,
                      ]}
                      testID="mobile-next-page-button"
                      accessibilityLabel="Next page"
                    >
                      <MaterialIcons
                        name="chevron-right"
                        size={20}
                        color={
                          currentPage >= totalPages ? "#ADB5BD" : isDark ? "#FFFFFF" : "#495057"
                        }
                      />
                    </TouchableOpacity>
                  </View>
                </View>
              </>
            ) : (
              <LHDataTable
                data={isLoading ? [] : filteredObservations}
                columns={tableColumns}
                tableHeader={""}
                tableDescription={
                  isLoading
                    ? ""
                    : `Showing ${filteredObservations.length} result(s)`
                }
                onRowClick={handleRowClick}
              />
            )}
          </View>
        </>
      )}
     </View>
  );
};

const styles = StyleSheet.create({
  metricsWrapper: {
    padding: 0,
  },
  headerBox: {
    // paddingHorizontal: 30,
    // paddingTop: 20,
    alignItems: 'flex-start',
    backgroundColor: 'transparent',
  },
  headerTitle: {
    // Typography/Font Family/Heading
    padding:8,
    fontFamily: FontFamily.bold,
    fontWeight: '700', // Bold
    fontStyle: 'normal',
    fontSize: 24, // Font Size/H5 (approx)
    lineHeight: 28, // Line Height/H5 (approx)
    letterSpacing: 0,
    color: '#3A3A3A',
    textAlign: 'left',
  },
  messageContainer: {
    padding: 16,
    alignItems: "center",
  },
  filterContainer: {
   marginTop:8,
   paddingVertical:20,
   paddingHorizontal:8,

    // marginBottom: "1%",
  },
  loadingText: {
    fontSize: 16,
    color: "#0066CC",
  },
  errorText: {
    fontSize: 16,
    color: "#DC3545",
  },
  TabbedNavigationContainer: {
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow:
          "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
        background:
          " #FFF",
        borderRadius:
           8,
      },
    }),
    backgroundColor: "#fff",
    borderRadius: 8,
    // marginTop: 16,
    marginBottom: 20,
    paddingLeft: Dimensions.get("window").width < 768 ? 12 : 24,
    paddingTop: Dimensions.get("window").width < 768 ? 12 : 24,
    paddingRight: Dimensions.get("window").width < 768 ? 24:0,
  },
  dataTableContainer: {
    ...Platform.select({
      ios: {
        shadowColor: "rgba(0, 94, 192, 0.08)",
        shadowOffset: { width: 4, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 24,
      },
      android: {
        elevation: 4,
        shadowColor: "rgba(0, 94, 192, 0.08)",
      },
      web: {
        boxShadow:
          "4px 0px 24px -2px rgba(0, 94, 192, 0.08)",
        background:
          " #FFF",
        borderRadius:
          " 8",
      },
    }),
    backgroundColor: "#fff",
    borderRadius: 8,
    // marginTop: 8,
   padding: 24,
  },
  dataTableContainerMobile: {
    backgroundColor: "transparent",
    borderRadius: 0,
    padding: 0,
  },
  headerBoxMobileSpacing: {
    marginBottom: 16,
  },
  // Mobile pagination styles (copied from LHDataTable)
  mobilePaginationContainer: {
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    paddingVertical: 16,
    backgroundColor: "transparent",
  },
  mobilePaginationBox: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "transparent",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#D7E5F8",
    overflow: "hidden",
  },
  mobilePaginationCell: {
    minHeight: 32,
    minWidth: 32,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  mobilePaginationArrowCell: {
    backgroundColor: "transparent",
  },
  mobilePaginationCurrentCell: {
    backgroundColor: "#005CBC",
  },
  mobilePaginationNextCell: {
    backgroundColor: "#FBFCFE",
  },
  mobilePaginationEmptyCell: {
    backgroundColor: "#FBFCFE",
    opacity: 0.3,
  },
  mobilePaginationCellDisabled: {
    opacity: 0.5,
  },
  mobilePaginationCurrentText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
    textAlign: "center",
  },
  mobilePaginationNextText: {
    fontSize: 14,
    fontWeight: "400",
    color: "#005CBC",
    textAlign: "center",
  },
  mobilePaginationCellText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#495057",
  },
  // Mobile filter button styles
  mobileFilterButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#034895", // From Figma design
    borderRadius: 24,
    paddingVertical: 4,
    paddingHorizontal: 16,
    height: 32,
    gap: 4,
  },
  mobileFilterButtonText: {
    fontSize: 14,
    fontStyle: "normal",
    fontWeight: "700",
    color: "#FFFFFF",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
});

export default ObservationMetrics;


